import React, { useCallback, useRef, useEffect } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { OnboardingState } from '@/types/onboarding';
import { StepWrapper } from './StepWrapper';
import { useOnboardingSteps } from '@/hooks/use-onboarding-steps';
import { OnboardingStepper } from './OnboardingStepper';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface OnboardingWizardProps {
  userId: string;
  currentState: OnboardingState;
  onStepComplete: (stepId: string, data: Record<string, any>) => void;
  onNavigateToStep?: (stepNumber: number) => void;
  allowNavigation?: boolean;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ 
  userId, 
  currentState,
  onStepComplete,
  onNavigateToStep,
  allowNavigation = true
}) => {
  // Ref para rastrear se o componente está montado
  const isMounted = useRef(true);
  const { toast } = useToast();
  
  // Definir isMounted como false no cleanup
  useEffect(() => {
    // Inicializar o ref
    isMounted.current = true;
    
    // Cleanup function para quando o componente desmontar
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Usar o hook personalizado para obter os passos e funções auxiliares
  const { 
    steps, 
    getStepByNumber, 
    isLastStep: isStepLast
  } = useOnboardingSteps();

  // Certificar que a etapa atual é válida
  const currentStep = Math.min(
    Math.max(0, typeof currentState?.current_step === 'number' ? currentState.current_step : 0), 
    steps.length - 1
  );
  
  // Obter o componente e informações para a etapa atual
  const currentStepObj = getStepByNumber(currentStep);
  const CurrentStepComponent = currentStepObj?.component;
  const isFirstStep = currentStep === 0;
  const isLastStep = isStepLast(currentStepObj?.id || '');
  
  // Manipulador seguro para completar uma etapa
  const handleStepComplete = useCallback((data: Record<string, any>) => {
    // Verificações defensivas
    if (!isMounted.current || !currentStepObj) {
      return;
    }
    
    try {
      // Obter o ID do passo atual
      const stepId = currentStepObj.id;
      
      // Chamar o manipulador do componente pai se estiver montado
      if (isMounted.current && typeof onStepComplete === 'function') {
        onStepComplete(stepId, data);
      }
    } catch (error) {
      console.error('Error in handleStepComplete:', error);
    }
  }, [currentStepObj, onStepComplete]);

  // Função para verificar e provisionar o n8n se necessário
  const checkAndProvisionN8n = useCallback(async () => {
    try {
      console.log("OnboardingWizard: Verificando se n8n já foi provisionado");
      
      // Obter a sessão atual para o token de autenticação
      const session = await supabase.auth.getSession();
      if (!session.data.session?.access_token) {
        console.error("OnboardingWizard: Sessão não disponível para verificar n8n");
        return false;
      }
      
      // Chamar a função para provisionar o n8n (workspace e workflow)
      // Como o endpoint verifica se já existe antes de criar novo, podemos chamar diretamente
      const { data: provisionData, error: provisionError } = await supabase.functions.invoke('n8n-provisioner', {
        body: { 
          action: "provision",
          userId: userId
        },
        headers: {
          Authorization: `Bearer ${session.data.session.access_token}`
        }
      });
      
      if (provisionError) {
        console.error("OnboardingWizard: Erro ao provisionar n8n:", provisionError);
        return false;
      }
      
      if (provisionData?.success) {
        // Se o workspace já existia, não mostrar mensagem
        if (!provisionData.workspace.isExisting) {
          console.log("OnboardingWizard: Workspace n8n provisionado com sucesso:", provisionData.workspace);
          
          toast({
            title: "Ambiente preparado",
            description: "Seu ambiente de automação foi configurado com sucesso!",
            variant: "default",
          });
        } else {
          console.log("OnboardingWizard: Workspace n8n já existe:", provisionData.workspace);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("OnboardingWizard: Erro ao verificar/provisionar n8n:", error);
      return false;
    }
  }, [userId, toast]);

  // Função para navegar para o próximo passo
  const handleNextStep = useCallback(async () => {
    if (!allowNavigation || !onNavigateToStep || isLastStep) return;
    
    // Verificar se estamos indo para a etapa final (completion)
    const targetStep = currentStep + 1;
    const targetStepObj = getStepByNumber(targetStep);
    
    // Se estamos indo para o último passo (completion), garantir que n8n está provisionado
    if (targetStepObj && targetStepObj.id === 'completion') {
      const isProvisioned = await checkAndProvisionN8n();
      
      if (!isProvisioned) {
        toast({
          title: "Aviso",
          description: "Não foi possível configurar seu ambiente n8n automaticamente. Você poderá fazer isso na próxima etapa.",
          variant: "warning",
        });
      }
    }
    
    onNavigateToStep(targetStep);
  }, [allowNavigation, currentStep, onNavigateToStep, isLastStep, getStepByNumber, checkAndProvisionN8n, toast]);

  // Função para navegar para o passo anterior
  const handlePreviousStep = useCallback(() => {
    if (!allowNavigation || !onNavigateToStep || isFirstStep) return;
    onNavigateToStep(currentStep - 1);
  }, [allowNavigation, currentStep, onNavigateToStep, isFirstStep]);

  // Verificar se o componente é válido e se existe
  const isValidStep = typeof CurrentStepComponent === 'function';
  
  // Se o componente não existe, mostrar um erro
  if (!isValidStep || !currentStepObj) {
    console.error(`Componente de passo inválido na posição ${currentStep}`);
    return (
      <div className="container mx-auto py-6 max-w-4xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar etapa</AlertTitle>
          <AlertDescription>
            Não foi possível carregar o componente da etapa atual. 
            Por favor, tente atualizar a página ou contate o suporte.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  // Obter os dados salvos para o passo atual
  const currentStepData = currentState?.steps_data?.[currentStepObj.id] || {};

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      {/* Title and step info */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold">{currentStepObj.title}</h1>
        <p className="text-muted-foreground">{currentStepObj.description}</p>
      </div>

      {/* New Onboarding Stepper */}
      <OnboardingStepper currentStep={currentStep} />

      {/* Current step component */}
      <StepWrapper onNext={handleStepComplete}>
        <CurrentStepComponent 
          data={currentStepData}
          isLastStep={isLastStep}
          onNext={() => {}} /* Será substituído pelo StepWrapper */
        />
      </StepWrapper>

      {/* Navigation buttons (only if navigation is allowed) */}
      {allowNavigation && onNavigateToStep && (
        <div className="flex justify-between mt-8">
          <Button 
            variant="outline"
            onClick={handlePreviousStep}
            disabled={isFirstStep}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button 
            variant="outline"
            onClick={handleNextStep}
            disabled={isLastStep}
            className="gap-1"
          >
            Próximo
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}; 